import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr, imageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCats(r.data));
  }, []);
  const catName = (id) => cats.find((c) => c.id === id)?.name || "-";
  const filtered = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Product deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Products</h1>
          <p className="text-hyd-text2 text-sm">{products.length} products in catalog</p>
        </div>
        <Link to="/admin/products/new">
          <Button data-testid={ADMIN.productNew} className="bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold">
            <Plus className="w-4 h-4 mr-1" /> New product
          </Button>
        </Link>
      </div>

      <div className="mt-4 relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-hyd-text3" />
        <Input placeholder="Search..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-hyd-panel border-hyd-border" />
      </div>

      <div className="mt-4 bg-hyd-panel border border-hyd-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-hyd-panel2 text-hyd-text2 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-3">Product</th>
              <th className="text-left p-3">Category</th>
              <th className="text-right p-3">Price</th>
              <th className="text-right p-3">Stock</th>
              <th className="text-left p-3">Tags</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hyd-border">
            {filtered.map((p) => (
              <tr key={p.id} className="hover:bg-hyd-panel2/60">
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded bg-hyd-panel2 overflow-hidden shrink-0">
                      {p.images?.[0] && <img src={imageUrl(p.images[0])} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div>
                      <div className="font-medium text-hyd-text line-clamp-1">{p.name}</div>
                      <div className="text-xs text-hyd-text3">{p.brand}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3 text-hyd-text2">{catName(p.category_id)}</td>
                <td className="p-3 text-right text-hyd-gold font-bold">
                  {p.price === null || p.price === undefined ? "Not set" : inr(p.price)}
                </td>
                <td className="p-3 text-right">{p.stock}</td>
                <td className="p-3 text-xs">
                  <div className="flex flex-wrap gap-1">
                    {p.is_bestseller && <Tag>Best</Tag>}
                    {p.is_new && <Tag>New</Tag>}
                    {p.is_green_cracker && <Tag color="emerald">Green</Tag>}
                    {!p.active && <Tag color="red">Hidden</Tag>}
                  </div>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Link to={`/admin/products/${p.id}`}>
                    <Button data-testid={ADMIN.productEdit(p.id)} variant="ghost" size="sm" className="text-hyd-gold hover:bg-hyd-panel">
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  </Link>
                  <Button data-testid={ADMIN.productDelete(p.id)} variant="ghost" size="sm" onClick={() => del(p)} className="text-hyd-red hover:bg-hyd-panel">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="p-10 text-center text-hyd-text3">No products</div>}
      </div>
    </div>
  );
}

function Tag({ children, color = "gold" }) {
  const cls = color === "gold"
    ? "bg-hyd-gold/15 text-hyd-gold"
    : color === "emerald"
    ? "bg-emerald-500/15 text-emerald-300"
    : "bg-hyd-red/15 text-hyd-red";
  return <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${cls}`}>{children}</span>;
}
